import React, { useEffect, useState } from "react";
import { Table, Button, Form, Alert, Spinner } from "react-bootstrap";
import EnrollmentService from "../../../_services/EnrollmentService";
import CollapsibleSection from "./CollapsibleSection";
import { todayLocalISO } from "./format";

interface PaymentFollowupSectionProps {
  enrollmentId: number;
}

const PaymentFollowupSection: React.FC<PaymentFollowupSectionProps> = ({
  enrollmentId,
}) => {
  const [followups, setFollowups] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [remarks, setRemarks] = useState("");
  const [followUpDate, setFollowUpDate] = useState(todayLocalISO());
  const [scheduledDate, setScheduledDate] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadFollowups = () => {
    setLoading(true);
    setLoadError(null);
    EnrollmentService.getPaymentFollowups(enrollmentId)
      .then((res: any) => {
        if (res.data?.success) setFollowups(res.data.data || []);
        else setLoadError(res.data?.message || "Failed to load follow-ups");
      })
      .catch((err: any) =>
        setLoadError(
          err.response?.data?.message ||
            err.message ||
            "Failed to load follow-ups",
        ),
      )
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadFollowups();
  }, [enrollmentId]);

  const handleAdd = async () => {
    if (!remarks.trim()) {
      setError("Remarks are required");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const res = await EnrollmentService.addPaymentFollowup(enrollmentId, {
        remarks: remarks.trim(),
        followUpDate,
        scheduledDate: scheduledDate || null,
      });
      if (res.data?.success) {
        setRemarks("");
        setScheduledDate("");
        setShowAddForm(false);
        loadFollowups();
      } else {
        setError(res.data?.message || "Failed to add follow-up");
      }
    } catch (err: any) {
      setError(
        err.response?.data?.message || err.message || "Failed to add follow-up",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <CollapsibleSection
      title="Payment Follow-up"
      count={followups.length}
      defaultOpen={false}
    >
      {loading ? (
        <div className="ev-loading">
          <i className="fa fa-spinner fa-spin" /> Loading follow-ups...
        </div>
      ) : loadError ? (
        <Alert variant="danger" style={{ fontSize: 12, marginBottom: 0 }}>
          {loadError}
        </Alert>
      ) : (
        <>
          <div style={{ marginBottom: 8 }}>
            <Button
              variant="primary"
              size="sm"
              style={{ fontSize: 11 }}
              onClick={() => {
                setShowAddForm((p) => !p);
                setError(null);
              }}
            >
              <i className="fa fa-plus" /> Add Follow-up
            </Button>
          </div>

          {showAddForm && (
            <div
              style={{
                background: "var(--surface-page)",
                padding: 10,
                borderRadius: 4,
                marginBottom: 10,
                fontSize: 12,
              }}
            >
              {error && (
                <Alert
                  variant="danger"
                  style={{ fontSize: 12, padding: "4px 10px" }}
                >
                  {error}
                </Alert>
              )}
              <Form.Group className="mb-2">
                <Form.Label style={{ fontSize: 11, fontWeight: 600 }}>
                  Follow-up Date
                </Form.Label>
                <Form.Control
                  type="date"
                  size="sm"
                  value={followUpDate}
                  onChange={(e) => setFollowUpDate(e.target.value)}
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label style={{ fontSize: 11, fontWeight: 600 }}>
                  Next Scheduled Date (optional)
                </Form.Label>
                <Form.Control
                  type="date"
                  size="sm"
                  value={scheduledDate}
                  onChange={(e) => setScheduledDate(e.target.value)}
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label style={{ fontSize: 11, fontWeight: 600 }}>
                  Remarks *
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  size="sm"
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  placeholder="Enter follow-up remarks..."
                />
              </Form.Group>
              <div style={{ display: "flex", gap: 6 }}>
                <Button
                  variant="primary"
                  size="sm"
                  style={{ fontSize: 11 }}
                  onClick={handleAdd}
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <Spinner role="status" animation="border" size="sm" /> Saving...
                    </>
                  ) : (
                    "Save"
                  )}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  style={{ fontSize: 11 }}
                  onClick={() => {
                    setShowAddForm(false);
                    setError(null);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {followups.length === 0 ? (
            <div className="ev-empty">No follow-up remarks</div>
          ) : (
            <Table
              bordered
              hover
              size="sm"
              style={{ fontSize: 12, marginBottom: 0 }}
            >
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Remarks</th>
                  <th>Scheduled</th>
                  <th>By</th>
                </tr>
              </thead>
              <tbody>
                {followups.map((f: any) => (
                  <tr key={f.id}>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {f.followUpDate || f.entryDate}
                    </td>
                    <td>{f.remarks}</td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {f.scheduledDate || "--"}
                    </td>
                    <td>{f.followedUpBy || "--"}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </>
      )}
    </CollapsibleSection>
  );
};

export default PaymentFollowupSection;
